import { useEffect, useState } from "react";
import { IMAGES } from "../constants/images";
import Sidebar from "../components/Sidebar";
import { FiChevronRight } from "react-icons/fi";
import { database } from "../constants/firebase";
import { ref, onValue } from "firebase/database";
import { ROUTES } from "../App";
import useMoveTo from "../constants/navigation";
import { useUser } from "../contexts/UserContext";
import { ICONS } from "../constants/icons";
import { useLocation, useParams } from "react-router";
import MobileHeader from "../components/MobileHeader";

const LessonsInChapter = () => {
    const navigate = useMoveTo();
    const { currentUser } = useUser();
    const { productId } = useParams();
    const location = useLocation();
    const chapterId = location.state?.chapterId;
    const [loadedImages, setLoadedImages] = useState({});
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [course, setCourse] = useState(null);
    const [hasPurchased, setHasPurchased] = useState(false);
    const [checkingAccess, setCheckingAccess] = useState(true);

    // ✅ Check if user has purchased the course
    useEffect(() => {
        if (!currentUser?.uid || !productId) return;

        const courseRef = ref(database, `Listings/${productId}`);
        const libraryRef = ref(database, `Users/${currentUser.uid}/library/${productId}`);

        // Load course info
        const unsubscribeCourse = onValue(courseRef, (snapshot) => {
            if (snapshot.exists()) {
                setCourse(snapshot.val());
            }
        });

        // Check library entry
        const unsubscribeLibrary = onValue(libraryRef, (snapshot) => {
            const exists = snapshot.exists();
            if (exists) {
                const data = snapshot.val();
                setHasPurchased(!!data.purchasePrice || !!data.totalPaid);
            } else {
                setHasPurchased(false);
            }
            setCheckingAccess(false);
        });

        return () => {
            unsubscribeCourse();
            unsubscribeLibrary();
        };
    }, [currentUser?.uid, productId]);

    // ✅ Check access and redirect if needed
    useEffect(() => {
        if (checkingAccess || !course) return;

        const coursePrice = parseFloat(course.price || 0);
        const isFree = coursePrice === 0;
        const isOwner = course.userId === currentUser?.uid;

        // If course is paid and user hasn't purchased it and isn't the owner, redirect
        if (!isFree && !hasPurchased && !isOwner) {
            window.showToast("Please purchase this course to access its content.", "info");
            navigate(`/product-details/${productId}`);
        }
    }, [checkingAccess, course, hasPurchased, currentUser?.uid, productId, navigate]);

    useEffect(() => {
        if (!currentUser || !productId || !chapterId) return;

        const lessonsRef = ref(database, `Listings/${productId}/chapters/${chapterId}/lessons`);
        const unsubscribe = onValue(lessonsRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                setLessons(Object.entries(data).map(([id, val]) => ({ id, ...val })));
            } else {
                setLessons([]);
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser, productId, chapterId]);

    const handleImageLoad = (id) => {
        setLoadedImages((prev) => ({ ...prev, [id]: true }));
    };

    const LessonSkeleton = () => (
        <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
                <div key={i} className="flex items-center justify-between border-t border-gray-200 dark:border-[#D9D9D9]/20 px-4 py-4 animate-pulse">
                    <div className="flex gap-4 max-w-[60%]">
                        <div className="w-16 h-16 rounded-lg bg-gray-300 dark:bg-gray-700" />
                        <div className="flex-1 space-y-2">
                            <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded w-20"></div>
                            <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded w-40"></div>
                        </div>
                    </div>
                    <div className="w-10 h-10 bg-gray-300 dark:bg-gray-700 rounded"></div>
                </div>
            ))}
        </div>
    );

    return (
        <div className="h-screen flex flex-col bg-white dark:bg-[#1A1A1A] text-black dark:text-white">
            <MobileHeader title="Lessons In Chapter" isBackBtn={true} />

            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 min-h-0 overflow-y-auto pt-5 pb-24 md:pb-20 px-6">
                    {/* Title */}
                    <div className="flex items-center justify-between">
                        <button onClick={() => navigate(-1)} className="flex-shrink-0 md:block hidden">
                            <img src={ICONS.arrowBack} className="w-4 h-4 md:w-5 md:h-5 grayscale brightness-0 dark:invert" alt="Back" />
                        </button>
                        <h2 className="text-2xl md:text-3xl font-semibold hidden md:block">Lessons In Chapter</h2>
                        <div className="w-5" />
                    </div>

                    {/* Lessons List */}
                    <div className="mt-3 space-y-4 md:mt-16">
                        {loading ? (
                            <LessonSkeleton />
                        ) : lessons.length === 0 ? (
                            <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                                No Lessons in this Chapter.
                            </div>
                        ) : (
                            lessons.map((item) => {
                                const isLoaded = loadedImages[item.id];
                                return (
                                    <div
                                        key={item.id}
                                        className="relative flex items-center justify-between border-t text-black dark:border-[#D9D9D9]/20 border-gray-200 dark:text-white px-4 py-4 hover:bg-gray-100 dark:hover:bg-[#2A2A2A] transition-colors duration-200 cursor-pointer hover:rounded-lg"
                                        onClick={() => navigate(`${ROUTES.VIEW_LESSON}/${productId}`, { state: { lessonId: item.id, chapterId } })}
                                    >
                                        <div className="flex items-center gap-4 max-w-[60%]">
                                            {/* Image with shimmer */}
                                            <div className="relative w-16 h-16">
                                                {!isLoaded && (
                                                    <div className="absolute inset-0 bg-gray-300 dark:bg-gray-700 rounded-lg animate-pulse"></div>
                                                )}
                                                <img
                                                    src={item.coverImageUrl || IMAGES.courses}
                                                    alt={item.title}
                                                    className={`w-16 h-16 rounded-lg object-cover transition-opacity duration-300 ${isLoaded ? "opacity-100" : "opacity-0"}`}
                                                    onLoad={() => handleImageLoad(item.id)}
                                                    onError={() => handleImageLoad(item.id)}
                                                />
                                            </div>

                                            <div>
                                                <div className="font-bold text-lg line-clamp-1">{item.title}</div>
                                                <div className="text-sm text-gray-700 dark:text-white line-clamp-3 min-w-full">
                                                    {item.description || "No description provided"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-right min-w-[40px] flex items-center justify-end">
                                            <FiChevronRight className="text-gray-700 dark:text-white text-xl" />
                                        </div>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </main>
            </div>
        </div>
    );
};

export default LessonsInChapter;
